/**
 * GridService.ts
 * 
 * 职责：
 * 1. 管理和绘制画布背景网格
 * 2. 提供网格属性的设置和获取功能，如网格大小、颜色和可见性
 * 3. 根据当前视图变换计算可见网格区域
 * 4. 负责绘制适应画布缩放和平移的网格线
 * 5. 优化网格渲染性能，只渲染可见区域
 * 6. 处理网格样式，如透明度和虚线效果
 * 7. 根据缩放比例自动控制网格显示
 */

export class GridService {
  private ctx: CanvasRenderingContext2D | null = null;
  private gridSize: number = 20;
  private gridColor: string = 'rgba(160, 160, 160, 0.51)'; // 浅灰色
  private showGrid: boolean = true; // 开关网格
  private minScaleForGrid: number = 0; // 显示网格的最小缩放值

  constructor() {
    // 构造函数
  }

  public setContext(ctx: CanvasRenderingContext2D): void {
    this.ctx = ctx;
  }

  public setGridSize(size: number): void {
    this.gridSize = size;
  }

  public getGridSize(): number {
    return this.gridSize;
  }

  public setGridColor(color: string): void {
    this.gridColor = color;
  }

  public getGridColor(): string {
    return this.gridColor;
  }

  public setShowGrid(show: boolean): void {
    this.showGrid = show;
  }

  public isShowGrid(): boolean {
    return this.showGrid;
  }

  // 设置显示网格的最小缩放阈值
  public setMinScaleForGrid(scale: number): void {
    this.minScaleForGrid = scale;
  }
  
  // 获取显示网格的最小缩放阈值
  public getMinScaleForGrid(): number {
    return this.minScaleForGrid;
  }

  public drawGrid(width: number, height: number): void {
    if (!this.ctx || !this.showGrid) {
      return;
    }

    const ctx = this.ctx;

    try {
      // 保存上下文
      ctx.save();

      // 获取当前变换
      const transform = ctx.getTransform();
      // 当前缩放值
      const currentScale = transform.a;
      
      // 如果缩放值小于最小阈值，不绘制网格
      if (currentScale < this.minScaleForGrid) {
        ctx.restore();
        return;
      }
      
      // 计算视口位置 (变换原点)
      const viewportX = -transform.e / transform.a;
      const viewportY = -transform.f / transform.d;
      
      // 计算网格起始点 (向下取整到网格大小的倍数)
      const startX = Math.floor(viewportX / this.gridSize) * this.gridSize;
      const startY = Math.floor(viewportY / this.gridSize) * this.gridSize;
      
      // 计算屏幕上可见的网格线数量
      const visibleWidth = width / transform.a;
      const visibleHeight = height / transform.d;
      
      // 设置网格样式，使用固定不透明度的网格线，不再动态计算透明度
      ctx.strokeStyle = this.gridColor;
      ctx.lineWidth = 0.5;
      
      // 移除虚线设置，使用实线以提高渲染性能
      ctx.setLineDash([]);

      // 绘制垂直网格线
      for (let x = startX; x <= startX + visibleWidth + this.gridSize; x += this.gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, startY);
        ctx.lineTo(x, startY + visibleHeight + this.gridSize);
        ctx.stroke();
      }

      // 绘制水平网格线
      for (let y = startY; y <= startY + visibleHeight + this.gridSize; y += this.gridSize) {
        ctx.beginPath();
        ctx.moveTo(startX, y);
        ctx.lineTo(startX + visibleWidth + this.gridSize, y);
        ctx.stroke();
      }
      
      // 恢复上下文
      ctx.restore();
    } catch (error) {
      // 确保上下文被恢复，即使出错
      ctx.restore();
    }
  }
}